/**
 * AI Training API Service
 * 
 * REST API client for AI-AMSO training endpoints.
 */

import { API_URL } from './api';
import {
    AITrainingSession,
    AITrainingSessionDetail,
    CreateTrainingSessionRequest,
    UpdateTrainingSessionRequest,
    VoiceCommandLog,
    VoiceCommandValidation,
    ParsedCommand,
} from '../types/aiTraining';

const BASE_URL = `${API_URL}/ai-training`;

// Helper to get auth headers
const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// =============================================================================
// TRAINING SESSION ENDPOINTS
// =============================================================================

/**
 * Create a new training session
 */
export async function createTrainingSession(
    data: CreateTrainingSessionRequest
): Promise<AITrainingSession> {
    const response = await fetch(`${BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to create training session');
    }

    return response.json();
}

/**
 * List training sessions
 */
export async function listTrainingSessions(params?: {
    project_id?: string;
    status?: string;
    skip?: number;
    limit?: number;
}): Promise<AITrainingSession[]> {
    const searchParams = new URLSearchParams();
    if (params?.project_id) searchParams.set('project_id', params.project_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const url = `${BASE_URL}/sessions${searchParams.toString() ? `?${searchParams}` : ''}`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch training sessions');
    }

    return response.json();
}

/**
 * Get training session details
 */
export async function getTrainingSession(
    sessionId: string
): Promise<AITrainingSessionDetail> {
    const response = await fetch(`${BASE_URL}/sessions/${sessionId}`, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Training session not found');
        }
        throw new Error('Failed to fetch training session');
    }

    return response.json();
}

/**
 * Update training session
 */
export async function updateTrainingSession(
    sessionId: string,
    data: UpdateTrainingSessionRequest
): Promise<AITrainingSession> {
    const response = await fetch(`${BASE_URL}/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to update training session');
    }

    return response.json();
}

/**
 * Start a training session
 */
export async function startTrainingSession(
    sessionId: string
): Promise<AITrainingSession> {
    const response = await fetch(`${BASE_URL}/sessions/${sessionId}/start`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to start training session');
    }

    return response.json();
}

/**
 * Complete a training session
 */
export async function completeTrainingSession(
    sessionId: string
): Promise<AITrainingSession> {
    const response = await fetch(`${BASE_URL}/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        throw new Error('Failed to complete training session');
    }

    return response.json();
}

/**
 * Delete a training session
 */
export async function deleteTrainingSession(
    sessionId: string
): Promise<void> {
    const response = await fetch(`${BASE_URL}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });

    if (!response.ok) {
        throw new Error('Failed to delete training session');
    }
}

// =============================================================================
// VOICE COMMAND ENDPOINTS
// =============================================================================

/**
 * List voice commands for a session
 */
export async function listVoiceCommands(
    sessionId: string,
    params?: { skip?: number; limit?: number }
): Promise<VoiceCommandLog[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const url = `${BASE_URL}/sessions/${sessionId}/commands${searchParams.toString() ? `?${searchParams}` : ''}`;

    const response = await fetch(url, {
        headers: authHeaders(),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch voice commands');
    }

    return response.json();
}

/**
 * Validate/correct a voice command
 */
export async function validateVoiceCommand(
    commandId: string,
    validation: VoiceCommandValidation
): Promise<VoiceCommandLog> {
    const response = await fetch(`${BASE_URL}/commands/${commandId}/validate`, {
        method: 'PATCH',
        headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(validation),
    });

    if (!response.ok) {
        throw new Error('Failed to validate voice command');
    }

    return response.json();
}

// =============================================================================
// NLP PARSING (for testing)
// =============================================================================

/**
 * Parse a transcription to test NLP
 */
export async function parseCommand(transcription: string): Promise<ParsedCommand> {
    const response = await fetch(`${BASE_URL}/parse?transcription=${encodeURIComponent(transcription)}`, {
        method: 'POST',
        headers: authHeaders(),
    });

    if (!response.ok) {
        throw new Error('Failed to parse command');
    }

    return response.json();
}
